import React, { useEffect, useState } from 'react';
import { Card, CardBody, Container } from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import PaginatedTable from "Components/Tables/PaginatedTable";
import useTableFilters from 'helpers/hooks/useTableFilters';
import Spinner from 'Components/UI/Spinner';
import convertDateFormat from 'Components/Date/DateFormat';
import { generateQueryParams } from 'helpers/queryParams';
import { getAllCustomers, updateCustomer } from 'helpers/API Calls/Operations/customer';
import ModalWrapper from 'Components/Modals/ModalWrapper';

const tableHead = [
  { id: 1, name: "#", isSortable: true, key: "id" },
  { id: 2, name: "Customer", isSortable: true, key: "name" },
  { id: 3, name: "Email", isSortable: true, key: "email" },
  { id: 4, name: "Address", isSortable: true, key: "address_line_1" },
  { id: 8, name: "Home Phone", isSortable: true, key: "phone_no_home" },
  { id: 9, name: "Mobile Phone", isSortable: true, key: "phone_no_mobile" },
  { id: 10, name: "Total Revenue", isSortable: true, key: "total_revenue" },
  { id: 11, name: "Marketing Emails", isSortable: true, key: "receive_marketing_emails" },
  { id: 12, name: "Status", isSortable: false, key: "status" },
  { id: 15, name: "Created", isSortable: true, key: "created_at" },
  { id: 16, name: "Updated", isSortable: true, key: "updated_at" },
  { id: 17, name: "Actions", isSortable: false, key: "actions" }
];


function Customers() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({current_page: 1, last_page: 1, per_page: 10, total: 0});

  const [modal, setModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [blacklistReason, setBlacklistReason] = useState<string>("");
  const [error, setError] = useState<string>("");
  
  const { filters, updateFilter } = useTableFilters();

  const fetchData = async () => {
    setIsLoading(true);
    const response = await getAllCustomers(generateQueryParams(filters)+`&status=active`);
    if (response.success) {
      setData(response.data.items);
      setPagination(response.data.pagination);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleClick = (item) => {
    setSelectedItem(item);
    setModal(true);
  };

  const handleConfirm = async () => {
    if(!blacklistReason.trim()) {
      setError("Please provide a reason for blacklisting the customer");
      return;
    }
    setError("");
    if (selectedItem) {
      const response = await updateCustomer(selectedItem.id, {
        status: "blocked",
        block_reason: blacklistReason
      });
      if (response.success) {
        setModal(false);
        setSelectedItem(null);
        setBlacklistReason("");
        fetchData();
      }
    }
  };

  const onCloseClick = () => {
    setModal(false);
    setSelectedItem(null);
    setBlacklistReason("");
    setError("");
  };

  return (
    <>
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Customers", link: "/customers" }]} />
        {isLoading ? (
          <div className="position-relative vh-100">
            <Spinner/>
          </div>
        ) : (
          <Card>
            <CardBody>
            <PaginatedTable
              tableHead={tableHead}
              pagination={pagination}
              updateFilter={updateFilter}
              filters={filters}
              > 
              {data.length > 0 && data.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>
                    <div>
                      {[
                      item.address_line_1,
                      item.address_line_2,
                      item.town,
                      item.complete_postcode
                    ].filter(Boolean).join(', ')}
                    </div>
                  </td>
                  <td>{item.phone_no_home || "-"}</td>
                  <td>{item.phone_no_mobile || "-"}</td>
                  <td>{Number(item.total_revenue).toFixed(2)}</td>
                  <td>{item.receive_marketing_emails ? 'Yes' : 'No'}</td>
                  <td><span className={`badge badge-soft-${item.status === 'active' ? 'success' : 'danger'}`}>
                    {item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}
                    </span>
                  </td>
                  <td>{convertDateFormat(item.created_at)}</td>
                  <td>{convertDateFormat(item.updated_at)}</td>
                  <td>
                    <div>
                      <button className="btn btn-danger btn-sm" onClick={() => handleClick(item)}><i className="mdi mdi-block-helper"></i></button>
                    </div>
                  </td>
                </tr>))
              }
              {!isLoading && data.length === 0 && <tr className="text-center"><td colSpan={tableHead.length} className="text-danger">No Data Found</td></tr>}
            </PaginatedTable>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
    {modal &&
    <ModalWrapper
      show={modal}
      onCloseClick={onCloseClick}
      title="Blacklist Customer"
      body={
        <form onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}>
          <div className="mb-3">
            <label htmlFor="blacklistReason" className="form-label">Reason for Blacklisting <span className='text-danger'>*</span></label>
            <textarea
              id="blacklistReason"
              className={`form-control ${error ? 'is-invalid' : ''}`}
              rows={3}
              value={blacklistReason}
              onChange={(e) => {
                setBlacklistReason(e.target.value);
                if(error) setError("");
              }}
              required
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </div>
          <div className="text-end">
            <button type="button" className="btn btn-light me-2" onClick={onCloseClick}>
              Cancel
            </button>
            <button type="submit" className="btn btn-danger">
              Blacklist Customer
            </button>
          </div>
        </form>
      }
    />}
    </>
  );
}

export default Customers;
